/**
 * EnrollOrLookupDeviceUseCase
 * Application layer — orchestrates the GET→enroll flow.
 *
 * Flow:
 *  1. Call repo.lookupByIdUnique(idUnique)
 *     - Device found (HTTP 200) → return { device, enrolled: false }
 *     - null (HTTP 404)         → proceed to step 2
 *  2. Call repo.enroll(params)  → return { device, enrolled: true }
 *
 * Errors propagate untouched:
 *  - DeviceAlreadyRegisteredError (from enroll on 4xx conflict)
 *  - DeviceEnrollmentFailedError  (from lookup or enroll on network/5xx)
 *
 * Design: enroll-device-on-login §1 (Architecture overview) + §2 (Decisions)
 * Layering: application → domain only; ZERO infrastructure imports.
 */
import type { IDeviceRepository } from '../../domain/repositories/IDeviceRepository';
import type { Device } from '../../domain/entities/Device';

/** [CONFIRM_WITH_PRODUCT] default model name for web kiosk terminals. */
const DEFAULT_MODEL_NAME = 'MyIntelli-Web-Kiosk';

export interface EnrollOrLookupResult {
  device: Device;
  /** true when the device was freshly enrolled (POST), false when it already existed (GET 200). */
  enrolled: boolean;
}

export interface EnrollOrLookupInput {
  idUnique: string;
  timezone: string;
}

export class EnrollOrLookupDeviceUseCase {
  constructor(private readonly repo: IDeviceRepository) { }

  async execute({ idUnique, timezone }: EnrollOrLookupInput): Promise<EnrollOrLookupResult> {
    const existing = await this.repo.lookupByIdUnique(idUnique);
    if (existing !== null) {
      return { device: existing, enrolled: false };
    }
    const device = await this.repo.enroll({
      idUnique,
      modelName: DEFAULT_MODEL_NAME,
      serial: idUnique,
      timezone,
      // idDevice intentionally absent on first enroll
    });

    return { device, enrolled: true };
  }
}
